import React, { useEffect, useId, useRef } from 'react'
import { Button, Card, Flex, Input, Select, Typography } from 'antd'
import { useTranslation } from 'react-i18next'
import { ConfigurationAnswer, ConfigurationQuestion } from '../types'

export type AnswerDraft = {
    values: string[]
    text: string
    other: string
    useOther: boolean
}

export const emptyAnswer: AnswerDraft = {
    values: [],
    text: '',
    other: '',
    useOther: false,
}

const otherValue = '__other__'

// More options than this use a dropdown instead of buttons.
const maxButtonOptions = 6

type ChoiceOption = {
    value: string
    label: string
}

export function answersComplete(questions: ConfigurationQuestion[], drafts: Record<string, AnswerDraft>) {
    return questions.every((question) => {
        const answer = drafts[question.id] ?? emptyAnswer

        if (answer.useOther && !answer.other.trim()) {
            return false
        }

        return !question.required || hasRequiredAnswer(question, answer)
    })
}

function hasRequiredAnswer(question: ConfigurationQuestion, answer: AnswerDraft) {
    if (question.type === 'text') {
        return !!answer.text.trim()
    }

    return answer.values.length > 0 || answer.useOther
}

export function serializeAnswers(
    questions: ConfigurationQuestion[],
    drafts: Record<string, AnswerDraft>
): ConfigurationAnswer[] {
    return questions.map((question) => {
        const answer = drafts[question.id] ?? emptyAnswer

        return {
            questionId: question.id,
            values: answer.values,
            text: answer.text.trim() || null,
            other: answer.useOther ? answer.other.trim() : null,
        }
    })
}

export function answered(question: ConfigurationQuestion, answer: AnswerDraft | undefined) {
    if (!answer) {
        return false
    }

    const hasOtherAnswer = answer.useOther && !!answer.other.trim()

    return !!answer.text.trim() || answer.values.length > 0 || hasOtherAnswer
}

type QuestionFormProps = {
    questions: ConfigurationQuestion[]
    value: Record<string, AnswerDraft>
    disabled: boolean
    onChange: (value: Record<string, AnswerDraft>) => void
    onSubmit: (value: Record<string, AnswerDraft>) => void
}

// All of Evaa's questions as one small form inside the chat.
export function QuestionForm({ questions, value, disabled, onChange, onSubmit }: QuestionFormProps) {
    const { t } = useTranslation()
    const card = useRef<HTMLDivElement>(null)
    const complete = answersComplete(questions, value)
    // One single-choice question is answered with one click.
    const oneClick = questions.length === 1 && questions[0].type === 'single_choice'
    const showSend = !oneClick || !!value[questions[0].id]?.useOther

    useEffect(() => {
        card.current?.querySelector<HTMLElement>('input, button')?.focus({ preventScroll: true })
    }, [])

    const update = (id: string, answer: AnswerDraft) => onChange({ ...value, [id]: answer })

    const submit = (next = value) => {
        if (!disabled && answersComplete(questions, next)) {
            onSubmit(next)
        }
    }

    const submitOnEnter = (event: React.KeyboardEvent<HTMLFormElement>) => {
        // Handle Enter here: the campaign wizard blocks Enter in inputs, and events bubble through the dialog portal.
        if (event.key !== 'Enter' || !(event.target as HTMLElement).dataset.submitOnEnter) {
            return
        }

        event.preventDefault()
        event.stopPropagation()
        submit()
    }

    const submitForm = (event: React.FormEvent<HTMLFormElement>) => {
        // React events bubble through the dialog portal into the campaign form.
        event.preventDefault()
        event.stopPropagation()
        submit()
    }

    const chooseAndSubmit = (questionId: string, option: string) =>
        submit({ [questionId]: { ...emptyAnswer, values: [option] } })

    return (
        <Card
            ref={card}
            size="small"
            className="w-full self-start rounded-2xl sm:max-w-[85%]"
        >
            <form
                onKeyDown={submitOnEnter}
                onSubmit={submitForm}
            >
                <fieldset
                    disabled={disabled}
                    className="m-0 min-w-0 border-0 p-0"
                >
                    <Flex
                        vertical
                        gap="middle"
                    >
                        {questions.map((question) => (
                            <QuestionField
                                key={question.id}
                                question={question}
                                answer={value[question.id] ?? emptyAnswer}
                                onChange={(answer) => update(question.id, answer)}
                                onChoose={oneClick ? (option) => chooseAndSubmit(question.id, option) : undefined}
                            />
                        ))}
                        {showSend && (
                            <Button
                                type="primary"
                                htmlType="submit"
                                className="self-end"
                                disabled={!complete}
                            >
                                {t('Send')}
                            </Button>
                        )}
                    </Flex>
                </fieldset>
            </form>
        </Card>
    )
}

type QuestionFieldProps = {
    question: ConfigurationQuestion
    answer: AnswerDraft
    onChange: (answer: AnswerDraft) => void
    // Set when choosing an option sends the form at once.
    onChoose?: (option: string) => void
}

function QuestionField({ question, answer, onChange, onChoose }: QuestionFieldProps) {
    const { t } = useTranslation()
    const id = useId()
    const helpId = question.help?.trim() ? `${id}-help` : undefined
    const multiple = question.type === 'multiple_choice'
    const otherOption = {
        value: otherValue,
        label: t('Other…'),
    }
    const options = [...question.options, ...(question.allowOther ? [otherOption] : [])]
    const selected = [...answer.values, ...(answer.useOther ? [otherValue] : [])]

    const select = (values: string[]) => {
        const useOther = values.includes(otherValue)

        onChange({
            ...answer,
            values: values.filter((value) => value !== otherValue),
            useOther,
            other: useOther ? answer.other : '',
        })
    }

    const toggle = (option: string) => {
        if (onChoose && option !== otherValue) {
            return onChoose(option)
        }

        const isSelected = selected.includes(option)

        if (multiple) {
            return select(isSelected ? selected.filter((value) => value !== option) : [...selected, option])
        }

        // A required single choice cannot be cleared by clicking it again.
        select(isSelected && !question.required ? [] : [option])
    }

    return (
        <Flex
            vertical
            gap={6}
        >
            <label
                id={`${id}-label`}
                htmlFor={question.type === 'text' ? `${id}-input` : undefined}
            >
                <Typography.Text strong>
                    {question.label}
                    {question.required && (
                        <Typography.Text
                            type="danger"
                            aria-hidden
                        >
                            {' *'}
                        </Typography.Text>
                    )}
                </Typography.Text>
            </label>
            {question.type === 'text' ? (
                <Input
                    id={`${id}-input`}
                    aria-describedby={helpId}
                    aria-required={question.required}
                    data-submit-on-enter
                    value={answer.text}
                    maxLength={2000}
                    onChange={(event) => onChange({ ...answer, text: event.target.value })}
                />
            ) : (
                <ChoiceInput
                    labelId={`${id}-label`}
                    helpId={helpId}
                    options={options}
                    selected={selected}
                    multiple={multiple}
                    onSelect={select}
                    onToggle={toggle}
                />
            )}
            {answer.useOther && (
                <Input
                    autoFocus
                    aria-label={`${question.label} — ${t('Other answer')}`}
                    placeholder={t('Enter your answer')}
                    data-submit-on-enter
                    value={answer.other}
                    maxLength={2000}
                    onChange={(event) => onChange({ ...answer, other: event.target.value })}
                />
            )}
            {helpId && (
                <Typography.Text
                    id={helpId}
                    type="secondary"
                    className="text-sm"
                >
                    {question.help}
                </Typography.Text>
            )}
        </Flex>
    )
}

type ChoiceInputProps = {
    labelId: string
    helpId?: string
    options: ChoiceOption[]
    selected: string[]
    multiple: boolean
    onSelect: (values: string[]) => void
    onToggle: (option: string) => void
}

// A dropdown for long option lists, buttons otherwise.
function ChoiceInput({ labelId, helpId, options, selected, multiple, onSelect, onToggle }: ChoiceInputProps) {
    if (options.length > maxButtonOptions) {
        return (
            <Select
                aria-labelledby={labelId}
                aria-describedby={helpId}
                mode={multiple ? 'multiple' : undefined}
                options={options}
                value={multiple ? selected : selected[0]}
                allowClear
                className="w-full"
                onChange={(value: string | string[] | undefined) => onSelect(selectedValues(value))}
            />
        )
    }

    return (
        <Flex
            wrap
            gap="small"
            role="group"
            aria-labelledby={labelId}
            aria-describedby={helpId}
        >
            {options.map((option) => (
                <Button
                    key={option.value}
                    shape="round"
                    type={selected.includes(option.value) ? 'primary' : 'default'}
                    aria-pressed={selected.includes(option.value)}
                    onClick={() => onToggle(option.value)}
                >
                    {option.label}
                </Button>
            ))}
        </Flex>
    )
}

function selectedValues(value: string | string[] | undefined): string[] {
    if (Array.isArray(value)) {
        return value
    }

    return value ? [value] : []
}
